import { createReducer } from 'utils/redux';
import { DeliverableCampaign, ThunkAction, TypedAction } from 'types';
import { API, destroy, get, post, put } from 'utils/api';
import { danger, success } from 'redux-store/reducers/common-reducer';
import { updateEntertainmentVideo as updateEntertainmentVideoDeliverable } from 'modules/subcampaigns/reducers/offerDeliverables';
import { __t, applyTranslations } from 'utils/language';
import { EntertainmentVideoAudioType, EntertainmentVideoItem, EntertainmentVideoItemBlank } from '../types';
import { updateCampaignEntertainmentVideo } from 'modules/campaigns/reducers/campaignDeliverables';

type PageType = 'offer' | 'content';

applyTranslations(require('./entertainment-videos-reducer.i18n.json'));

function translate(path: string) {
  return __t(`entertainmentVideoReducer${path}`);
}

export interface State {
  isEditMode: boolean,
  isUpdating: boolean,
  isDeclining: boolean,
  isPublishing: boolean,
  isUnpublishing: boolean
}

const initialState: State = {
  isEditMode: false,
  isUpdating: false,
  isDeclining: false,
  isPublishing: false,
  isUnpublishing: false
};

enum ActionType {
  SET_IS_EDIT_MODE = 'entertainmentVideos/SET_IS_EDIT_MODE',
  SET_IS_UPDATING = 'entertainmentVideos/SET_IS_UPDATING',
  SET_IS_DECLINING = 'entertainmentVideos/SET_IS_DECLINING',
  SET_IS_PUBLISHING = 'entertainmentVideos/SET_IS_PUBLISHING',
  SET_IS_UNPUBLISHING = 'entertainmentVideos/SET_IS_UNPUBLISHING'
}

export function setIsEditMode(isEditMode: boolean): TypedAction<boolean> {
  return {
    type: ActionType.SET_IS_EDIT_MODE,
    payload: isEditMode
  };
}

function setIsUpdating(isUpdating: boolean): TypedAction<boolean> {
  return {
    type: ActionType.SET_IS_UPDATING,
    payload: isUpdating
  };
}

function setIsDeclining(isDeclining: boolean): TypedAction<boolean> {
  return {
    type: ActionType.SET_IS_DECLINING,
    payload: isDeclining
  };
}

function setIsPublishing(isPublishing: boolean): TypedAction<boolean> {
  return {
    type: ActionType.SET_IS_PUBLISHING,
    payload: isPublishing
  };
}

function setIsUnpublishing(isUnpublishing: boolean): TypedAction<boolean> {
  return {
    type: ActionType.SET_IS_UNPUBLISHING,
    payload: isUnpublishing
  };
}

function getGroupedEntertainmentVideoItems(
  currentItems: Array<EntertainmentVideoItem>,
  updatedItems: Array<EntertainmentVideoItem | EntertainmentVideoItemBlank>
): {
  toAdd: Array<EntertainmentVideoItemBlank>,
  toDelete: Array<EntertainmentVideoItem>
} {
  const toAdd: Array<EntertainmentVideoItemBlank> = [];
  const toDelete: Array<EntertainmentVideoItem> = [];

  currentItems.forEach(item => {
    const isDeleted = !updatedItems.find(
      updatedItem => updatedItem.sku === item.sku
    );

    if (isDeleted) {
      toDelete.push(item);
    }
  });

  updatedItems.forEach(item => {
    const isNew = !('id' in item);

    if (isNew) {
      toAdd.push(item);
    }
  });

  return { toAdd, toDelete };
}

interface UpdateEntertainmentVideoPayload {
  pageType: PageType,
  offerId: number,
  deliverableId: number,
  entertainmentVideoId: number,
  data: {
    name: string,
    genders: Array<string>,
    tags: Array<string>,
    audioType: EntertainmentVideoAudioType | null,
    isProductReview: boolean,
    publishedFrom: string | null,
    publishedUntil: string | null,
    publishedCountries: Array<string>,
    videoUrl: string | null,
    thumbnailUrl: string | null,
    items: Array<EntertainmentVideoItem | EntertainmentVideoItemBlank>
  }
}

export function updateEntertainmentVideo({
  pageType,
  offerId,
  deliverableId,
  entertainmentVideoId,
  data
}: UpdateEntertainmentVideoPayload): ThunkAction {
  return async (dispatch, getState) => {
    dispatch(setIsUpdating(true));

    try {
      let deliverables: Array<DeliverableCampaign> = [];

      if (pageType === 'offer') {
        deliverables =
          getState().offerDeliverables.deliverablesByOfferId[offerId];
      }

      if (pageType === 'content') {
        deliverables = getState().campaignDeliverables.deliverables;
      }

      const entertainmentVideos =
        deliverables.find(deliverable => deliverable.id === deliverableId)
          ?.entertainmentVideos ?? [];
      const entertainmentVideo = entertainmentVideos.find(ev => ev.entertainmentVideo.id === entertainmentVideoId);

      if (!entertainmentVideo) {
        /**
         * Shouldn't happen normally
         */
        throw new Error(
          `Entertainment video ${entertainmentVideoId} doesn't exist for deliverable ${deliverableId} and offer ${offerId}`
        );
      }

      const { items, videoUrl, thumbnailUrl, ...updatePayload } = data;

      /**
       * Prepare adding / deleting entertainment video items
       */
      const groupedItems = getGroupedEntertainmentVideoItems(
        entertainmentVideo.items,
        items
      );

      const updateUrl = API.END_POINTS.ENTERTAINMENT_VIDEOS.updateEntertainmentVideo(
        deliverableId,
        entertainmentVideoId
      );

      await put(updateUrl, { data: updatePayload });

      const hasVideoChanged = entertainmentVideo.media?.url !== videoUrl;
      const hasThumbnailChanged = entertainmentVideo.media?.thumbnailUrl !== thumbnailUrl;

      if (hasVideoChanged || hasThumbnailChanged) {
        const updateEntertainmentVideoMediaUrl = API.END_POINTS.ENTERTAINMENT_VIDEOS.updateEntertainmentVideoMediaUrls(
          deliverableId,
          entertainmentVideoId
        );

        await put(updateEntertainmentVideoMediaUrl, {
          data: {
            url: videoUrl,
            thumbnailUrl
          }
        });
      }

      if (groupedItems.toAdd.length > 0) {
        const addItemUrl =
          API.END_POINTS.ENTERTAINMENT_VIDEOS.addEntertainmentVideoItem(
            deliverableId,
            entertainmentVideoId
          );

        await Promise.all(
          groupedItems.toAdd.map(item => post(addItemUrl, { data: item }))
        );
      }

      if (groupedItems.toDelete.length > 0) {
        await Promise.all(
          groupedItems.toDelete.map(item => {
            const deleteItemUrl =
              API.END_POINTS.ENTERTAINMENT_VIDEOS.deleteEntertainmentVideoItem(
                deliverableId,
                entertainmentVideoId,
                item.id
              );

            return destroy(deleteItemUrl);
          })
        );
      }

      const response = await get(
        API.END_POINTS.ENTERTAINMENT_VIDEOS.getEntertainmentVideo(
          deliverableId,
          entertainmentVideoId
        )
      );

      if (pageType === 'offer') {
        dispatch(
          updateEntertainmentVideoDeliverable(
            offerId,
            deliverableId,
            entertainmentVideoId,
            response.data
          )
        );
      }

      if (pageType === 'content') {
        dispatch(
          updateCampaignEntertainmentVideo(
            deliverableId,
            entertainmentVideoId,
            response.data
          )
        );
      }

      dispatch(success(translate('.actions.update.success')));
      dispatch(setIsEditMode(false));
    } catch (error) {
      const errorMessage = error.response?.data?.detail;

      dispatch(
        danger(errorMessage ?? translate('.actions.errorMessageFallback'))
      );
    } finally {
      dispatch(setIsUpdating(false));
    }
  };
}

interface DeclineEntertainmentVideoPayload {
  pageType: PageType,
  offerId: number,
  deliverableId: number,
  entertainmentVideoId: number,
  declinedReason: string
}

export function declineEntertainmentVideo({
  pageType,
  offerId,
  deliverableId,
  entertainmentVideoId,
  declinedReason
}: DeclineEntertainmentVideoPayload): ThunkAction {
  return async (dispatch) => {
    dispatch(setIsDeclining(true));

    try {
      const url = API.END_POINTS.ENTERTAINMENT_VIDEOS.declineEntertainmentVideo(
        deliverableId,
        entertainmentVideoId
      );

      await put(url, { data: { declinedReason } });

      const response = await get(
        API.END_POINTS.ENTERTAINMENT_VIDEOS.getEntertainmentVideo(
          deliverableId,
          entertainmentVideoId
        )
      );

      if (pageType === 'offer') {
        dispatch(
          updateEntertainmentVideoDeliverable(
            offerId,
            deliverableId,
            entertainmentVideoId,
            response.data
          )
        );
      }

      if (pageType === 'content') {
        dispatch(
          updateCampaignEntertainmentVideo(
            deliverableId,
            entertainmentVideoId,
            response.data
          )
        );
      }

      dispatch(success(translate('.actions.decline.success')));
    } catch (error) {
      const errorMessage = error.response?.data?.detail;

      dispatch(
        danger(errorMessage ?? translate('.actions.errorMessageFallback'))
      );
    } finally {
      dispatch(setIsDeclining(false));
    }
  };
}

interface PublishEntertainmentVideoPayload {
  pageType: PageType,
  offerId: number,
  deliverableId: number,
  entertainmentVideoId: number
}

export function publishEntertainmentVideo({
  pageType,
  offerId,
  deliverableId,
  entertainmentVideoId
}: PublishEntertainmentVideoPayload): ThunkAction {
  return async (dispatch) => {
    dispatch(setIsPublishing(true));

    try {
      const publishUrl = API.END_POINTS.ENTERTAINMENT_VIDEOS.publishEntertainmentVideo(
        deliverableId,
        entertainmentVideoId
      );

      await put(publishUrl);

      const response = await get(
        API.END_POINTS.ENTERTAINMENT_VIDEOS.getEntertainmentVideo(
          deliverableId,
          entertainmentVideoId
        )
      );

      if (pageType === 'offer') {
        dispatch(
          updateEntertainmentVideoDeliverable(
            offerId,
            deliverableId,
            entertainmentVideoId,
            response.data
          )
        );
      }

      if (pageType === 'content') {
        dispatch(
          updateCampaignEntertainmentVideo(
            deliverableId,
            entertainmentVideoId,
            response.data
          )
        );
      }

      dispatch(success(translate('.actions.publish.success')));
    } catch (error) {
      const errorMessage = error.response?.data?.detail;

      dispatch(
        danger(errorMessage ?? translate('.actions.errorMessageFallback'))
      );
    } finally {
      dispatch(setIsPublishing(false));
    }
  };
}

interface UnpublishEntertainmentVideo {
  pageType: PageType,
  offerId: number,
  deliverableId: number,
  entertainmentVideoId: number
}

export function unpublishEntertainmentVideo({
  pageType,
  offerId,
  deliverableId,
  entertainmentVideoId
}: UnpublishEntertainmentVideo): ThunkAction {
  return async (dispatch) => {
    dispatch(setIsUnpublishing(true));

    try {
      const unpublishUrl = API.END_POINTS.ENTERTAINMENT_VIDEOS.unpublishEntertainmentVideo(
        deliverableId, entertainmentVideoId
      );

      await put(unpublishUrl);

      const response = await get(
        API.END_POINTS.ENTERTAINMENT_VIDEOS.getEntertainmentVideo(
          deliverableId,
          entertainmentVideoId
        )
      );

      if (pageType === 'offer') {
        dispatch(
          updateEntertainmentVideoDeliverable(
            offerId,
            deliverableId,
            entertainmentVideoId,
            response.data
          )
        );
      }

      if (pageType === 'content') {
        dispatch(
          updateCampaignEntertainmentVideo(
            deliverableId,
            entertainmentVideoId,
            response.data
          )
        );
      }

      dispatch(success(translate('.actions.unpublish.success')));
    } catch (error) {
      const errorMessage = error.response?.data?.detail;

      dispatch(
        danger(errorMessage ?? translate('.actions.errorMessageFallback'))
      );
    } finally {
      dispatch(setIsUnpublishing(false));
    }
  };
}

export default createReducer<State, any>('entertainmentVideos', initialState, {
  [ActionType.SET_IS_EDIT_MODE]: (state, payload) => ({
    ...state,
    isEditMode: payload
  }),
  [ActionType.SET_IS_UPDATING]: (state, payload) => ({
    ...state,
    isUpdating: payload
  }),
  [ActionType.SET_IS_DECLINING]: (state, payload) => ({
    ...state,
    isDeclining: payload
  }),
  [ActionType.SET_IS_PUBLISHING]: (state, payload) => ({
    ...state,
    isPublishing: payload
  })
});
